import { deriveSeed } from "@eldrun/shared";
import { MonotoneCurve, smoothstep } from "./curves.ts";
import { fbm, ridged, Simplex2 } from "./noise.ts";
import type { TerrainParams } from "./params.ts";

/**
 * Curva altura(t): fundo do mar até 0, praia quase plana, planícies/colinas e serra,
 * sem platô no topo (doc 04, Forma da ilha). t = 0,20 é o nível do mar.
 */
export const HEIGHT_CURVE = new MonotoneCurve([
  [0.0, -24],
  [0.12, -10],
  [0.2, 0],
  [0.25, 2.5],
  [0.4, 14],
  [0.6, 38],
  [0.7, 58],
  [0.8, 85],
  [0.95, 110],
  [1.4, 125],
]);
export const SEA_T = 0.2;
/** t em que a curva atinge ~45 m (início da serra por altitude). */
export const SERRA_T = 0.6395;

/** Carimbo de terreno de um POI (doc 04): plano até `inner`, transição até `outer`, na altura `target`. */
export interface Stamp {
  x: number;
  z: number;
  inner: number;
  outer: number;
  target: number;
}

/** Célula do índice espacial dos carimbos (doc 04: hash de 128 m). */
const STAMP_CELL = 128;
const STAMP_CELLS = 16;

/** Campo de terreno de uma tentativa: funções puras de (x, z) em metros. */
export class TerrainField {
  readonly params: TerrainParams;
  /**
   * Remapeamento linear escolhido pelo planejador por percentis (doc 04, Δmar):
   * elevação = SEA_T + (baseT − seaBaseT) × landScale. `seaBaseT` fixa a área emersa;
   * `landScale` fixa a fração de serra. Ambos são escalares: os chunks continuam independentes.
   */
  seaBaseT = SEA_T;
  landScale = 1;
  private readonly warpX: Simplex2;
  private readonly warpZ: Simplex2;
  private readonly continental: Simplex2;
  private readonly ridge: Simplex2;
  private readonly ridgeMask: Simplex2;
  private readonly moistureNoise: Simplex2;
  private readonly detail: Simplex2;
  private readonly invRadius: number;
  /** Carimbos dos POIs por célula de 128 m (vazio até o planejador de POIs rodar). */
  private stampCells: Stamp[][] = [];

  constructor(terrainSeed: number, params: TerrainParams) {
    this.params = params;
    this.warpX = new Simplex2(deriveSeed(terrainSeed, "warp-x"));
    this.warpZ = new Simplex2(deriveSeed(terrainSeed, "warp-z"));
    this.continental = new Simplex2(deriveSeed(terrainSeed, "continental"));
    this.ridge = new Simplex2(deriveSeed(terrainSeed, "serra"));
    this.ridgeMask = new Simplex2(deriveSeed(terrainSeed, "mascara-serra"));
    this.moistureNoise = new Simplex2(deriveSeed(terrainSeed, "umidade"));
    this.detail = new Simplex2(deriveSeed(terrainSeed, "detalhe"));
    this.invRadius = 1 / params.radius;
  }

  /** Elevação normalizada antes do ajuste do mar. */
  baseT(x: number, z: number): number {
    const p = this.params;
    const px = x + fbm(this.warpX, x, z, p.warp) * p.warp.amplitude;
    const pz = z + fbm(this.warpZ, x, z, p.warp) * p.warp.amplitude;
    const d = Math.sqrt(px * px + pz * pz) * this.invRadius;
    const mask = 1 - smoothstep(p.falloffStart, 1, d);
    if (mask <= 0) return 0;
    const e = 0.5 + 0.5 * fbm(this.continental, px, pz, p.continental);
    const interior = 1 - smoothstep(0.35, 0.85, d);
    const ridgeMask = smoothstep(0.42, 0.62, 0.5 + 0.5 * fbm(this.ridgeMask, x, z, p.ridgeMask)) * interior;
    const s = ridgeMask > 0 ? ridged(this.ridge, x, z, p.ridge) * ridgeMask : 0;
    return mask * (0.3 + 0.7 * e) + p.ridge.weight * s * mask * mask;
  }

  /** Converte a elevação bruta na altura sem detalhe. */
  heightFromBase(baseT: number): number {
    return HEIGHT_CURVE.evaluate(SEA_T + (baseT - this.seaBaseT) * this.landScale);
  }

  /** Altura sem o detalhe de alta frequência (usada pelo planejador). */
  coarseHeight(x: number, z: number): number {
    return this.heightFromBase(this.baseT(x, z));
  }

  /** Altura final do terreno em metros: o detalhe low-poly só entra acima da praia; os carimbos dos POIs por último. */
  height(x: number, z: number): number {
    const y = this.unstampedHeight(x, z);
    return this.stampCells.length > 0 ? this.stamp(x, z, y) : y;
  }

  /** Altura sem os carimbos (os POIs são postos sobre ela, sem dependência circular). */
  unstampedHeight(x: number, z: number): number {
    const y = this.coarseHeight(x, z);
    if (y <= 3) return y;
    const w = smoothstep(3, 6, y);
    return y + w * this.params.detail.amplitude * fbm(this.detail, x, z, this.params.detail);
  }

  /**
   * Instala os carimbos (doc 04, Carimbos de terreno): altura = lerp(base, alvo, w), com
   * w = 1 − smoothstep(interno; externo; distância). Consulta só os da célula de 128 m.
   */
  setStamps(stamps: readonly Stamp[]): void {
    const cells: Stamp[][] = Array.from({ length: STAMP_CELLS * STAMP_CELLS }, () => []);
    for (const s of stamps) {
      if (s.outer <= 0) continue;
      const c0 = this.stampCell(s.x - s.outer);
      const c1 = this.stampCell(s.x + s.outer);
      const r0 = this.stampCell(s.z - s.outer);
      const r1 = this.stampCell(s.z + s.outer);
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) (cells[r * STAMP_CELLS + c] as Stamp[]).push(s);
    }
    this.stampCells = stamps.length > 0 ? cells : [];
  }

  private stampCell(v: number): number {
    return Math.min(STAMP_CELLS - 1, Math.max(0, Math.floor((v + STAMP_CELL * STAMP_CELLS * 0.5) / STAMP_CELL)));
  }

  private stamp(x: number, z: number, y: number): number {
    const list = this.stampCells[this.stampCell(z) * STAMP_CELLS + this.stampCell(x)] as Stamp[];
    let out = y;
    for (const s of list) {
      const dx = x - s.x;
      const dz = z - s.z;
      const d2 = dx * dx + dz * dz;
      if (d2 >= s.outer * s.outer) continue;
      const w = 1 - smoothstep(s.inner, s.outer, Math.sqrt(d2));
      out += (s.target - out) * w;
    }
    return out;
  }

  /** Umidade em [0, 1]: ruído + bônus perto da costa − secura com a altitude. */
  moisture(x: number, z: number, y: number, coastDistance: number): number {
    const base = 0.5 + 0.5 * fbm(this.moistureNoise, x, z, this.params.moisture);
    const coastal = 0.25 * (1 - smoothstep(0, 180, coastDistance));
    const dryness = 0.004 * (y > 20 ? y - 20 : 0);
    const m = base + coastal - dryness;
    return m < 0 ? 0 : m > 1 ? 1 : m;
  }
}
